// Pricing · Rate Card Admin (Finance-gated). Versioned + FULLY in-place editable:
// fork the active card into a DRAFT → click any value to edit it inline (tier
// prices, included counts, volume bands, term discounts, modules, wholesale) →
// Publish to make it active. Optimistic local update + PATCH; publishing applies
// to NEW quotes only (existing licenses grandfathered via licenses.price_version).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PageHeader, Card, CardHead, Table, Td, Row, Mono, Btn } from '@/components/shared/primitives';
import { Pill } from '@/components/shared/badges';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/shared/ToastProvider';
import { PERSONA } from '@/lib/persona';
import {
  getPriceBookVersions, getPriceBookVersion, createPriceBookDraft, patchPriceBookDraft, publishPriceBook,
  type PriceBookFull, type PriceBookVersionRow,
} from '@/lib/api/pricing';

const SALES = PERSONA.sales.accent;

// ── Click-to-edit-in-place numeric cell ──────────────────────────────────────
function EditableCell({ value, editable, onCommit, prefix, suffix, dp = 0, align = 'right' }: {
  value: number; editable: boolean; onCommit: (n: number) => void; prefix?: string; suffix?: string; dp?: number; align?: 'left' | 'right';
}) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(String(value));
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) { setV(String(value)); setTimeout(() => ref.current?.select(), 10); } }, [editing, value]);

  const display = `${prefix || ''}${dp ? value.toFixed(dp) : value.toLocaleString()}${suffix || ''}`;
  if (!editable) return <Mono style={{ fontWeight: 600 }}>{display}</Mono>;
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} title="Click to edit"
        style={{ fontFamily: 'var(--mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', background: 'transparent', border: 'none', borderBottom: '1px dashed var(--border-strong)', cursor: 'pointer', padding: '1px 2px' }}>
        {display}
      </button>
    );
  }
  const commit = () => {
    setEditing(false);
    const n = parseFloat(v);
    if (!Number.isNaN(n) && n !== value) onCommit(n);
  };
  return (
    <input ref={ref} type="number" step="any" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      style={{ width: 84, padding: '4px 6px', borderRadius: 6, border: `1px solid ${SALES}`, fontSize: 12.5, fontFamily: 'var(--mono)', textAlign: align, boxSizing: 'border-box' }} />
  );
}

function DraftDialog({ open, onCreate, onCancel }: { open: boolean; onCreate: (v: string, eff: string) => void; onCancel: () => void }) {
  const [ver, setVer] = useState('');
  const [eff, setEff] = useState(new Date().toISOString().slice(0, 10));
  useEffect(() => { if (open) { setVer(''); setEff(new Date().toISOString().slice(0, 10)); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent style={{ maxWidth: 420 }}>
        <DialogTitle style={{ fontSize: 15.5, fontWeight: 700 }}>Draft a new rate card version</DialogTitle>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: -8 }}>Forks the current active card into an editable draft.</div>
        <div>
          <label style={lbl}>Version label</label>
          <input autoFocus value={ver} onChange={(e) => setVer(e.target.value)} placeholder="FY26.2" style={inp} />
          <label style={{ ...lbl, marginTop: 12 }}>Effective date</label>
          <input type="date" value={eff} onChange={(e) => setEff(e.target.value)} style={inp} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onCancel} style={btnGhost}>Cancel</button>
          <button onClick={() => ver.trim() && onCreate(ver.trim(), eff)} disabled={!ver.trim()} style={{ ...btnPri, opacity: ver.trim() ? 1 : 0.5 }}>Create draft</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PricingAdmin() {
  const [versions, setVersions] = useState<PriceBookVersionRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [book, setBook] = useState<PriceBookFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const loadVersions = useCallback(async (preferred?: string) => {
    const vs = await getPriceBookVersions();
    setVersions(vs);
    const draft = vs.find((v) => v.status === 'draft');
    const live = vs.find((v) => v.status === 'published');
    const pick = preferred || draft?.version || live?.version || vs[0]?.version || null;
    setSelected(pick);
    if (pick) setBook(await getPriceBookVersion(pick));
  }, []);
  useEffect(() => { loadVersions().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load rate card')); }, [loadVersions]);

  const selectVersion = async (v: string) => { setSelected(v); try { setBook(await getPriceBookVersion(v)); } catch { /* keep */ } };
  const editable = book?.status === 'draft';

  // Optimistic in-place commit: update local book immediately, PATCH the slice,
  // revert from server on error.
  const commit = async (patch: Record<string, unknown>) => {
    if (!book) return;
    const prev = book;
    setBook({ ...book, ...patch } as PriceBookFull);
    try {
      await patchPriceBookDraft(book.version, patch);
    } catch (e) {
      setBook(prev);
      toast({ kind: 'error', title: 'Save failed', body: e instanceof Error ? e.message : 'Could not save.' });
    }
  };

  const createDraft = async (version: string, eff: string) => {
    setDraftOpen(false);
    try { await createPriceBookDraft(version, eff); await loadVersions(version); toast({ kind: 'success', title: 'Draft created', body: `${version} is editable — click any value to change it.` }); }
    catch (e) { toast({ kind: 'error', title: 'Draft failed', body: e instanceof Error ? e.message : 'Could not create draft (version may already exist).' }); }
  };
  const doPublish = async () => {
    if (!book) return;
    setPublishOpen(false);
    try { await publishPriceBook(book.version); await loadVersions(book.version); toast({ kind: 'success', title: `${book.version} published`, body: 'Now active for new quotes; existing licenses grandfathered.' }); }
    catch (e) { toast({ kind: 'error', title: 'Publish failed', body: e instanceof Error ? e.message : 'Could not publish.' }); }
  };

  if (error) return <div style={{ padding: 24 }}><AlertBanner kind="error">{error}</AlertBanner></div>;
  if (!book) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading rate card…</div>;

  return (
    <div>
      <PageHeader
        title="Rate Card Admin"
        subtitle="Edit & version the price book — changes apply to NEW quotes; existing licenses are grandfathered to renewal"
        actions={
          <>
            <Btn icon="git-branch" onClick={() => setDraftOpen(true)}>Draft new version</Btn>
            <Btn kind="primary" accent={SALES} icon="upload" disabled={!editable} onClick={() => setPublishOpen(true)}>Publish</Btn>
          </>
        }
      />

      {editable ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--ps-pill-green-bg)', border: '1px solid var(--ps-pill-green-bg)', borderRadius: 10, marginBottom: 16, fontSize: 13, color: 'var(--ps-pill-green-tx)' }}>
          <Icon name="pencil" size={15} color={SALES} />
          <span>Editing draft <strong>{book.version}</strong> — click any <span style={{ borderBottom: '1px dashed var(--border-strong)' }}>underlined</span> value to change it; press Enter or click away to save.</span>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--accent-light)', border: '1px solid var(--accent-muted)', borderRadius: 10, marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
          <Icon name="lock" size={15} color="var(--accent)" />
          <span>Viewing the <strong>{book.status === 'published' ? 'live' : book.status === 'archived' ? 'superseded' : book.status}</strong> version <strong>{book.version}</strong> (read-only). Use <strong>Draft new version</strong> to fork an editable copy.</span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--ps-pill-amber-bg)', border: '1px solid var(--ps-pill-amber-bg)', borderRadius: 10, marginBottom: 16, fontSize: 13, color: 'var(--ps-pill-amber-tx)' }}>
        <Icon name="alert-triangle" size={15} color="#D97706" />
        <span><strong>Blast radius:</strong> publishing changes pricing for <strong>new quotes only</strong>. Existing licenses keep their contracted price until renewal, then move with notice + delta.</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.45fr 1fr', gap: 16, alignItems: 'start' }}>
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title={editable ? 'Tiers — editable' : 'Tiers'} right={<Pill bg={editable ? 'var(--ps-pill-amber-bg)' : 'var(--ps-pill-blue-bg)'} fg={editable ? 'var(--ps-pill-amber-tx)' : 'var(--ps-pill-blue-tx)'}>{book.version}{editable ? ' · draft' : ''}</Pill>} />
          <Table cols={[{ label: 'Tier' }, { label: 'Base / mo', align: 'right' }, { label: 'Vendors incl.', align: 'right' }, { label: '+ / vendor', align: 'right' }, { label: 'Orgs incl.', align: 'right' }, { label: '+ / org', align: 'right' }, { label: 'Overage', align: 'right' }]}>
            {book.tiers.map((t) => {
              const set = (field: string, n: number) => commit({ tiers: book.tiers.map((x) => x.tier === t.tier ? { ...x, [field]: n } : x) });
              return (
                <Row key={t.tier}>
                  <Td style={{ fontWeight: 600 }}>{t.name}</Td>
                  <Td align="right"><EditableCell value={t.base} editable={editable} prefix="$" onCommit={(n) => set('base', n)} /></Td>
                  <Td align="right"><EditableCell value={t.vendorIncluded} editable={editable} onCommit={(n) => set('vendorIncluded', n)} /></Td>
                  <Td align="right"><EditableCell value={t.vendorSeat} editable={editable} prefix="$" dp={2} onCommit={(n) => set('vendorSeat', n)} /></Td>
                  <Td align="right"><EditableCell value={t.orgIncluded} editable={editable} onCommit={(n) => set('orgIncluded', n)} /></Td>
                  <Td align="right"><EditableCell value={t.orgSeat} editable={editable} prefix="$" onCommit={(n) => set('orgSeat', n)} /></Td>
                  <Td align="right"><EditableCell value={t.overage} editable={editable} prefix="$" dp={2} onCommit={(n) => set('overage', n)} /></Td>
                </Row>
              );
            })}
          </Table>
        </Card>
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title="Version history" />
          <div style={{ padding: '8px 8px' }}>
            {versions.map((h) => {
              const on = h.version === selected;
              return (
                <button key={h.version} onClick={() => selectVersion(h.version)} style={{ width: '100%', display: 'flex', gap: 12, padding: '11px 12px', borderRadius: 8, border: 'none', background: on ? 'var(--ps-pill-green-bg)' : 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                  <Icon name={h.status === 'published' ? 'check-circle' : h.status === 'draft' ? 'git-branch' : 'archive'} size={15} color={h.status === 'published' ? SALES : h.status === 'draft' ? '#B45309' : 'var(--text-muted)'} style={{ marginTop: 1 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{h.version} {h.status === 'published' && <span style={{ fontSize: 11, color: SALES }}>· live</span>}{h.status === 'draft' && <span style={{ fontSize: 11, color: '#B45309' }}>· draft</span>}{h.status === 'archived' && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· superseded</span>}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Effective {h.effective_date}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Volume bands — ranges + prices all editable in place */}
      <Card style={{ overflow: 'hidden', marginTop: 16 }}>
        <CardHead title="Volume bands" sub="Graduated per-seat rates — click a boundary or price to edit" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
          {([['Vendor seats', book.vendorBands, 2, 'vendorBands'] as const, ['Org seats', book.orgBands, 0, 'orgBands'] as const]).map(([title, bands, dp, key], idx) => (
            <div key={title} style={{ borderRight: idx === 0 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', background: 'var(--bg-muted)' }}>{title}</div>
              {bands.map((b, i) => {
                const setBand = (field: string, n: number) => commit({ [key]: bands.map((x, j) => j === i ? { ...x, [field]: n } : x) });
                const isInf = b.to == null || b.to >= 1e9;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 16px', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <EditableCell value={b.from} editable={editable} align="left" onCommit={(n) => setBand('from', n)} />
                      <span style={{ color: 'var(--text-muted)' }}>–</span>
                      {isInf ? <Mono style={{ color: 'var(--text-secondary)' }}>∞</Mono> : <EditableCell value={b.to} editable={editable} align="left" onCommit={(n) => setBand('to', n)} />}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <EditableCell value={b.price} editable={editable} prefix="$" dp={dp} onCommit={(n) => setBand('price', n)} />
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>/seat</span>
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </Card>

      {/* Terms + wholesale */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16, alignItems: 'start' }}>
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title="Term discounts" sub="Annual commitment discounts (editable %)" />
          <Table cols={[{ label: 'Term' }, { label: 'Discount %', align: 'right' }]}>
            {book.terms.map((tm) => (
              <Row key={tm.years}>
                <Td style={{ fontWeight: 600 }}>{tm.years} year{tm.years > 1 ? 's' : ''}</Td>
                <Td align="right"><EditableCell value={Math.round(tm.discount * 100)} editable={editable} suffix="%" onCommit={(n) => commit({ terms: book.terms.map((x) => x.years === tm.years ? { ...x, discount: n / 100 } : x) })} /></Td>
              </Row>
            ))}
          </Table>
        </Card>
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title="Platform (reseller) wholesale" sub="Discount off retail for partner sales" />
          <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: SALES, display: 'flex', alignItems: 'center', gap: 4 }}>
              −<EditableCell value={book.wholesaleDiscountPct} editable={editable} suffix="%" onCommit={(n) => commit({ wholesaleDiscountPct: n })} />
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>applied to the full additive price for platform sales. Org (direct) sales pay retail.</div>
          </div>
        </Card>
      </div>

      {/* Modules */}
      <Card style={{ overflow: 'hidden', marginTop: 16 }}>
        <CardHead title="Modules & add-ons" sub="One source for the License Matrix, Quote Builder and entitlements (editable price)" />
        <Table cols={[{ label: 'Module' }, { label: 'Key' }, { label: 'Price / mo', align: 'right' }]}>
          {book.modules.map((m) => (
            <Row key={m.key}>
              <Td style={{ fontWeight: 600 }}>{m.name}</Td>
              <Td><Mono style={{ color: 'var(--text-secondary)' }}>{m.key}</Mono></Td>
              <Td align="right"><EditableCell value={m.price} editable={editable} prefix="$" onCommit={(n) => commit({ modules: book.modules.map((x) => x.key === m.key ? { ...x, price: n } : x) })} /></Td>
            </Row>
          ))}
        </Table>
      </Card>

      <DraftDialog open={draftOpen} onCreate={createDraft} onCancel={() => setDraftOpen(false)} />
      <ConfirmDialog open={publishOpen} title={`Publish ${book.version}?`} body="This becomes the active rate card for new quotes immediately. Existing licenses keep their contracted price until renewal." confirmLabel="Publish" onConfirm={doPublish} onCancel={() => setPublishOpen(false)} />
    </div>
  );
}

const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 };
const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', background: 'var(--surface)', color: 'var(--text-primary)' };
const btnGhost: React.CSSProperties = { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
const btnPri: React.CSSProperties = { padding: '8px 16px', borderRadius: 8, border: 'none', background: SALES, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };

export default PricingAdmin;
